import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
import { ComiteNonOperationalBanner } from '../components/ComiteNonOperationalBanner';
import { Alert, Button, Card, Modal } from '@aions/ui-primitives';
import './TriagemPage.css';

type Gravidade = 'baixa' | 'media' | 'alta' | 'critica';
type Rota = 'padrao' | 'com_membro_externo';

const GRAVIDADES: readonly Gravidade[] = ['baixa', 'media', 'alta', 'critica'];
const ROTAS: readonly Rota[] = ['padrao', 'com_membro_externo'];

/** Item de `/api/comite/members` — projeção mínima do worker. */
interface MembroLista {
  id: string;
  email: string;
  subRole: string | null;
  comiteRecurso?: boolean;
}

interface ConflitosInfo {
  total: number;
  declarados: number;
  pendentes: number;
}

interface TriagemHistorico {
  ordem: number;
  memberId: string | null;
  email: string | null;
  subRole: string | null;
  gravidade: Gravidade | null;
  rota: Rota | null;
  createdWeek: string | null;
  /** Data e hora da classificação (decisão do operador 2026-08-13). A projeção
   *  `triagens` do worker descartava o campo; agora o expõe. `null` para entradas
   *  gravadas antes da mudança — elas realmente não têm hora. */
  createdAtEpochMs: number | null;
  reclassificacao: boolean;
}

export function TriagemPage({
  caseId,
  onBack,
  onDone,
}: {
  caseId: string;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session } = useSession();
  const [gravidade, setGravidade] = useState<Gravidade>('media');
  const [rota, setRota] = useState<Rota>('padrao');
  // Phase 2.2 D-213 — flag para ADM-03 (alta diretoria/RH envolvidos).
  const [envolveAltaDiretoria, setEnvolveAltaDiretoria] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonOperationalMissing, setNonOperationalMissing] = useState<readonly string[] | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  // Conflito de interesse — contagem vinda do GET /:id (só números, sem IDs). O
  // popup abre no mount quando o colegiado ainda não fechou a atestação.
  const [conflitos, setConflitos] = useState<ConflitosInfo | null>(null);
  const [showConflitoModal, setShowConflitoModal] = useState(false);
  /** Este leitor conduz o caso sozinho? Resposta AUTORITATIVA do servidor
   *  (`conduzUnipessoal` = role `admin_master` E marco de assunção). A tela não
   *  recalcula o predicado — ver o comentário no `CaseDetailPage`. */
  const [unipessoal, setUnipessoal] = useState(false);
  const [triagens, setTriagens] = useState<readonly TriagemHistorico[]>([]);
  /** Ids dos exigíveis que ainda NÃO classificaram (`triagensPendentes` do worker).
   *  O painel existia só com quem JÁ classificou, e por isso não conseguia responder
   *  "quem falta" — quem abrisse antes dos colegas via uma linha (a própria) e nada
   *  que indicasse haver mais gente esperada. */
  const [pendentes, setPendentes] = useState<readonly string[]>([]);
  /** id → e-mail. O worker manda IDs em `triagensPendentes` de propósito (guard
   *  anti-over-disclosure do detalhe do caso); o rótulo humano vem do `/members`,
   *  exatamente como o `CaseDetailPage` já resolve `pareceresPendentes`. */
  const [emailPorId, setEmailPorId] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * Candidatos a membro EXTERNO — o pool de contas `comite_recurso`.
   *
   * 🔴 VEM DE ROTA PRÓPRIA, e não do roster do caso. As duas listas respondem perguntas
   * COMPLEMENTARES: o roster é quem JÁ ESTÁ no caso (e por construção nunca contém
   * `comite_recurso`); esta é quem PODE SER CHAMADO. Filtrar candidatos sobre o roster
   * devolvia lista vazia SEMPRE, e a tela afirmava "nenhuma conta cadastrada" com contas
   * cadastradas — mentira sobre o cadastro, mandando falar com o admin de TI à toa.
   */
  const [candidatosExterno, setCandidatosExterno] = useState<readonly MembroLista[]>([]);
  /** Quem foi escolhido no seletor. `''` = ninguém ainda. */
  const [externoId, setExternoId] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}`);
        if (!resp.ok) return; // fail-safe: sem dado → sem aviso (não bloqueia a triagem)
        const body = (await resp.json()) as {
          conflitos?: ConflitosInfo;
          triagens?: readonly TriagemHistorico[];
          triagensPendentes?: readonly string[];
          conduzUnipessoal?: boolean;
        };
        if (cancelled) return;
        setUnipessoal(body.conduzUnipessoal === true);
        if (body.conflitos) {
          setConflitos(body.conflitos);
          if (body.conflitos.pendentes > 0) setShowConflitoModal(true);
        }
        if (body.triagens) setTriagens(body.triagens);
        if (body.triagensPendentes) setPendentes(body.triagensPendentes);
      } catch {
        // silencioso — o aviso é auxiliar, nunca trava a tela
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  // Rótulos dos membros. Falha aqui NÃO esconde ninguém: sem o mapa a linha cai no
  // fallback de conta desconhecida, que é pior de ler mas continua contando a pessoa.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
        if (!resp.ok) return;
        const body = (await resp.json()) as { members?: MembroLista[] };
        if (cancelled || !body.members) return;
        setEmailPorId(new Map(body.members.map((m) => [m.id, m.email])));
      } catch {
        // idem — auxiliar
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pool de candidatos a membro externo. Busca SEMPRE, não só quando a rota externa está
  // escolhida: o seletor aparece no instante do clique, e buscar ali deixaria um piscar de
  // "nenhuma conta cadastrada" antes de a resposta chegar — a mesma mensagem falsa, só que
  // por um segundo.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/candidatos-externo`);
        if (!resp.ok) return;
        const body = (await resp.json()) as { candidatos?: MembroLista[] };
        if (!cancelled) setCandidatosExterno(body.candidatos ?? []);
      } catch {
        // auxiliar — lista vazia degrada para o aviso, que nesse caso é verdadeiro
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  // O caso já foi classificado ao menos uma vez → enviar agora é RECLASSIFICAÇÃO.
  const jaClassificado = triagens.length > 0;

  // Phase 2.2 D-214 — checkbox condicional: gravidade ∈ {alta, critica}.
  const showEnvolveCheckbox = gravidade === 'alta' || gravidade === 'critica';

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNonOperationalMissing(null);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/triagem`, {
        method: 'POST',
        body: JSON.stringify({
          gravidade,
          rota,
          // Phase 2.2 D-213 — sempre envia bool; checkbox oculto = false explicit
          // (sem ambiguidade vs Phase 2 client old).
          envolveAltaDiretoria: showEnvolveCheckbox && envolveAltaDiretoria,
        }),
      });
      // Phase 2.2 D-211 — 503 comitê non-operational.
      if (resp.status === 503) {
        const body = (await resp.json().catch(() => ({}))) as { missing?: readonly string[] };
        setNonOperationalMissing(body.missing ?? []);
        return;
      }
      if (!resp.ok) {
        // Guarda o CÓDIGO do servidor, não o texto: a tradução acontece na
        // renderização (padrão das outras 8 telas).
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'error_unknown');
        return;
      }
      // 🔴 A INDICAÇÃO É UM SEGUNDO ATO, e só vai DEPOIS da triagem — o servidor recusa
      // com `rota_nao_permite_externo` enquanto a rota gravada não for `com_membro_externo`.
      // Não é chamada redundante: são dois registros distintos na trilha append-only (a
      // classificação e a indicação), com autores e momentos próprios.
      //
      // Falha aqui NÃO desfaz a triagem — ela já está gravada, e reverter exigiria um ato
      // de desclassificação que não existe. Por isso o erro é mostrado NOMEANDO o que
      // ficou pendente, em vez de um "erro ao salvar" genérico que sugeriria que nada foi
      // gravado: a pessoa precisa saber que deve indicar de novo, não classificar de novo.
      if (rota === 'com_membro_externo' && externoId !== '') {
        const ri = await apiFetch(`/api/comite/cases/${caseId}/membro-externo`, {
          method: 'POST',
          body: JSON.stringify({ externoId }),
        });
        if (!ri.ok) {
          const body = (await ri.json().catch(() => ({}))) as { error?: string };
          setError(`externo:${body.error ?? 'error_unknown'}`);
          return;
        }
      }
      onDone();
    } finally {
      setSubmitting(false);
    }
  };

  const pctDeclarados =
    conflitos && conflitos.total > 0
      ? Math.round((conflitos.declarados / conflitos.total) * 100)
      : 0;

  return (
    <div className="triagem-shell">
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>

      {/* Phase 2.2 D-211 — banner crítico se comitê não-operacional (polling 30s). */}
      <ComiteNonOperationalBanner />

      {/* Popup de conflito de interesse — abre no mount se houver pendentes.
          ── COPY CONDICIONAL À CONDUÇÃO UNIPESSOAL (operador, 2026-08-13)

          A frase padrão diz "o rito só fecha quando todo o colegiado se manifestar".
          Sob condução unipessoal isso é FALSO: não há colegiado a esperar, e as
          declarações dos outros membros não bloqueiam este rito.

          O popup NÃO foi removido, e a escolha é deliberada. A informação continua útil
          na primeira vez: ela confirma ao gestor que ele não está pulando etapa — que
          era exatamente a dúvida do relato ("não consigo realizar a triagem sozinho").
          Um popup que aparece e não explica nada é o que ensina a dispensar popups. */}
      {showConflitoModal && conflitos && (
        <Modal
          title={
            unipessoal
              ? t('triagem.conflito.popup_title_unipessoal')
              : t('triagem.conflito.popup_title')
          }
          titleId="triagem-conflito-title"
          onClose={() => setShowConflitoModal(false)}
          actions={
            <Button onClick={() => setShowConflitoModal(false)}>{t('triagem.conflito.ack')}</Button>
          }
        >
          <div className="triagem-conflito-modal__lead">
            <span className="triagem-conflito-modal__icon" aria-hidden="true">
              ⚠️
            </span>
            <p style={{ margin: 0 }}>
              {unipessoal
                ? t('triagem.conflito.popup_body_unipessoal')
                : t('triagem.conflito.popup_body', {
                    count: conflitos.pendentes,
                    total: conflitos.total,
                  })}
            </p>
          </div>
          {/* O MEDIDOR e a contagem saem sob condução unipessoal: eles medem o avanço
              do COLEGIADO, que aqui não é a régua de nada. Mostrar "1/5" ao gestor que
              conduz sozinho sugere um progresso que ele precisa completar — a barra
              quase vazia é justamente o que faz parecer que falta algo. */}
          {!unipessoal && (
            <>
              <div
                className="triagem-conflito-modal__meter"
                role="progressbar"
                aria-valuenow={pctDeclarados}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span style={{ width: `${pctDeclarados}%` }} />
              </div>
              <p className="triagem-conflito-modal__count">
                {conflitos.declarados}/{conflitos.total} membros declararam conflito de interesse.
              </p>
            </>
          )}
        </Modal>
      )}

      <Card>
        <div className="triagem-head">
          <div className="triagem-head__icon" aria-hidden="true">
            ⚖️
          </div>
          <div>
            <h2 className="triagem-head__title">{t('triagem.title')}</h2>
            <p className="triagem-head__sub">{t('triagem.subtitle')}</p>
            <span className="triagem-head__case">
              {t('triagem.case_label')} <code>{caseId}</code>
            </span>
          </div>
        </div>

        {/* Aviso persistente na página (além do popup) quando há pendências. */}
        {conflitos && conflitos.pendentes > 0 && (
          <Alert variant="warning" role="status" style={{ marginBottom: 16 }}>
            {t('triagem.conflito.banner', {
              pendentes: conflitos.pendentes,
              total: conflitos.total,
            })}
          </Alert>
        )}

        {/* Aviso de reclassificação — o caso já tem classificação registrada. */}
        {jaClassificado && (
          <Alert variant="warning" role="status" style={{ marginBottom: 16 }}>
            <strong>↻ {t('triagem.reclassif.notice_title')}</strong>
            <p style={{ margin: '4px 0 0' }}>{t('triagem.reclassif.notice_body')}</p>
          </Alert>
        )}

        {nonOperationalMissing !== null && (
          <Alert variant="error" data-testid="non-operational-alert" style={{ marginBottom: 16 }}>
            <strong>{t('comite.non_operational.title')}</strong>
            <p style={{ margin: '4px 0 0' }}>
              {t('comite.non_operational.detail', { missing: nonOperationalMissing.join(', ') })}
            </p>
          </Alert>
        )}
        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {t(`triagem.errors.${error}`, { defaultValue: t('triagem.error_unknown') })}
          </Alert>
        )}

        {/* Histórico — todas as contas que já classificaram (reclassif. marcadas),
            SEGUIDO de quem ainda não classificou. Os dois blocos juntos cobrem o
            roster exigível inteiro: sem o segundo, abrir a tela antes dos colegas
            mostrava uma linha só (a sua) e nada dizia que havia mais gente esperada. */}
        {(triagens.length > 0 || pendentes.length > 0) && (
          <section className="triagem-hist" data-testid="triagem-historico">
            <h3 className="triagem-hist__title">{t('triagem.historico.title')}</h3>
            <ol className="triagem-hist__list">
              {triagens.map((tr) => {
                const souEu = !!session && tr.memberId === session.user.id;
                const conta =
                  tr.email ??
                  (tr.memberId ? emailPorId.get(tr.memberId) : undefined) ??
                  tr.subRole ??
                  t('triagem.historico.unknown_account');
                return (
                  <li
                    key={`${tr.ordem}-${tr.memberId ?? 'x'}`}
                    className="triagem-hist__item"
                    data-reclassif={tr.reclassificacao ? 'true' : 'false'}
                  >
                    <span className="triagem-hist__badge">
                      {tr.reclassificacao
                        ? t('triagem.historico.reclassificacao_badge')
                        : t('triagem.historico.original_badge')}
                    </span>
                    <div className="triagem-hist__main">
                      <span className="triagem-hist__account">
                        {conta}
                        {souEu && (
                          <span className="triagem-hist__you"> ({t('triagem.historico.you')})</span>
                        )}
                      </span>
                      <span className="triagem-hist__meta">
                        {tr.gravidade && (
                          <span className="triagem-hist__grav" data-accent={tr.gravidade}>
                            <span className="triagem-opt__dot" aria-hidden="true" />
                            {t(`triagem.gravidades.${tr.gravidade}`)}
                          </span>
                        )}
                        {(tr.createdAtEpochMs !== null || tr.createdWeek) && (
                          <span className="triagem-hist__week">
                            {tr.createdAtEpochMs !== null
                              ? new Date(tr.createdAtEpochMs).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : t('triagem.historico.week', { week: tr.createdWeek })}
                          </span>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
              {/* Quem ainda não classificou. Mesma <ol> de propósito: uma lista só
                  responde "como está a classificação do comitê"; duas obrigariam o
                  leitor a cruzar nomes entre elas para chegar na mesma resposta. */}
              {pendentes.map((memberId) => {
                const souEu = !!session && memberId === session.user.id;
                const conta = emailPorId.get(memberId) ?? t('triagem.historico.unknown_account');
                return (
                  <li
                    key={`pend-${memberId}`}
                    className="triagem-hist__item"
                    data-pendente="true"
                    data-testid="triagem-historico-pendente"
                  >
                    <span className="triagem-hist__badge">
                      {t('triagem.historico.pendente_badge')}
                    </span>
                    <div className="triagem-hist__main">
                      <span className="triagem-hist__account">
                        {conta}
                        {souEu && (
                          <span className="triagem-hist__you"> ({t('triagem.historico.you')})</span>
                        )}
                      </span>
                      <span className="triagem-hist__meta">
                        <span className="triagem-hist__week">
                          {t('triagem.historico.pendente_meta')}
                        </span>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          {/* ---- Gravidade ---- */}
          <fieldset className="triagem-group" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="triagem-group__label">{t('triagem.gravidade_label')}</legend>
            <p className="triagem-group__help">{t('triagem.gravidade_help')}</p>
            <div className="triagem-options triagem-options--grav">
              {GRAVIDADES.map((g) => (
                <label
                  key={g}
                  className={`triagem-opt${gravidade === g ? ' is-selected' : ''}`}
                  data-accent={g}
                  data-testid={`gravidade-opt-${g}`}
                >
                  <input
                    type="radio"
                    name="gravidade"
                    value={g}
                    checked={gravidade === g}
                    onChange={() => setGravidade(g)}
                  />
                  <span className="triagem-opt__title">
                    <span className="triagem-opt__dot" aria-hidden="true" />
                    {t(`triagem.gravidades.${g}`)}
                  </span>
                  <span className="triagem-opt__desc">{t(`triagem.gravidade_desc.${g}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* ---- Rota ---- */}
          <fieldset className="triagem-group" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="triagem-group__label">{t('triagem.rota_label')}</legend>
            <p className="triagem-group__help">{t('triagem.rota_help')}</p>
            <div className="triagem-options">
              {ROTAS.map((r) => {
                // Dica de quando escolher esta rota. `defaultValue: ''` porque só a
                // rota externa tem explicação hoje — rota sem dica não ganha balão nem
                // `aria-describedby` apontando para string vazia (alguns leitores de
                // tela anunciam isso como pausa sem conteúdo).
                const dica = t(`triagem.rota_tooltip.${r}`, { defaultValue: '' });
                return (
                  <label
                    key={r}
                    className={`triagem-opt${rota === r ? ' is-selected' : ''}`}
                    data-testid={`rota-opt-${r}`}
                  >
                    <input
                      type="radio"
                      name="rota"
                      value={r}
                      checked={rota === r}
                      onChange={() => setRota(r)}
                      {...(dica !== '' ? { 'aria-describedby': `rota-tip-${r}` } : {})}
                    />
                    <span className="triagem-opt__title">
                      <span className="triagem-opt__dot" aria-hidden="true" />
                      {t(`triagem.rotas.${r}`)}
                    </span>
                    {/* Mesmo desenho do `acao-tip` do CaseDetailPage, e pelo mesmo
                        motivo: o texto fica SEMPRE no DOM, ligado por
                        `aria-describedby`, e some só VISUALMENTE (CSS
                        `visibility`/`opacity`, nunca `display:none`). Leitor de tela
                        não tem hover — trocar por `title` do navegador removeria a
                        explicação de quem mais precisa dela. */}
                    {dica !== '' && (
                      <span
                        className="triagem-opt__tip"
                        role="tooltip"
                        id={`rota-tip-${r}`}
                        data-testid={`rota-tip-${r}`}
                      >
                        {dica}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* SELETOR DO MEMBRO EXTERNO — só na rota que o justifica.
              Aparece condicionado à rota de propósito: um seletor sempre visível
              convidaria a indicar alguém fora da hipótese que o ADR cobre, e o servidor
              recusaria com `rota_nao_permite_externo` depois do trabalho todo. */}
          {rota === 'com_membro_externo' && (
            <div className="triagem-group" data-testid="externo-picker-group">
              <label className="triagem-group__label" htmlFor="externo-select">
                {t('triagem.externo.label')}
              </label>
              <p className="triagem-group__help">{t('triagem.externo.help')}</p>
              {candidatosExterno.length === 0 ? (
                /* Lista vazia é INFORMAÇÃO, não campo desabilitado em silêncio: significa
                   que ninguém foi cadastrado com o papel, e quem lê precisa saber que a
                   saída é falar com o admin de TI — não ficar procurando o nome. */
                <Alert variant="warning" role="status" data-testid="externo-sem-candidatos">
                  {t('triagem.externo.sem_candidatos')}
                </Alert>
              ) : (
                <select
                  id="externo-select"
                  className="triagem-select"
                  data-testid="externo-select"
                  value={externoId}
                  onChange={(e) => setExternoId(e.target.value)}
                >
                  <option value="">{t('triagem.externo.placeholder')}</option>
                  {candidatosExterno.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.email}
                    </option>
                  ))}
                </select>
              )}
              {externoId !== '' && (
                <p className="triagem-group__help" data-testid="externo-aviso-votacao">
                  {t('triagem.externo.aviso_votacao')}
                </p>
              )}
            </div>
          )}

          {/* Phase 2.2 D-213 + D-214 — checkbox condicional + warning. */}
          {showEnvolveCheckbox && (
            <div className="triagem-group" data-testid="envolve-alta-diretoria-group">
              <label className="triagem-toggle">
                <input
                  type="checkbox"
                  checked={envolveAltaDiretoria}
                  onChange={(e) => setEnvolveAltaDiretoria(e.target.checked)}
                  data-testid="envolve-alta-diretoria-checkbox"
                />
                <span>{t('triagem.envolve_alta_diretoria_label')}</span>
              </label>
              {envolveAltaDiretoria && (
                <Alert
                  variant="warning"
                  role="status"
                  data-testid="externo-warning"
                  style={{ marginTop: 8 }}
                >
                  ⚠ {t('triagem.warning_externo_obrigatorio')}
                </Alert>
              )}
            </div>
          )}

          <div className="triagem-actions">
            <Button type="button" variant="ghost" onClick={onBack}>
              {t('case_detail.back')}
            </Button>
            <Button type="submit" loading={submitting}>
              {submitting
                ? t('triagem.submitting')
                : jaClassificado
                  ? t('triagem.reclassif.submit')
                  : t('triagem.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
